/**
 * Bulk firmware-upgrade preflight rules — which dry-run rows an operator may
 * include in a batch, and which the confirm dialog refuses to launch.
 *
 * The preflight classifies each identifier against the chosen image. Three
 * tiers come out of it:
 *
 * - **Selectable** — safe, or risky-but-the-operator's-call. Risky rows start
 *   unchecked and the "include all warnings" quick action ticks them.
 * - **Blocking** — a model mismatch. The image was built for different
 *   hardware; shipping it can brick the unit, so no override reaches these
 *   rows and a batch that somehow contains one cannot be started.
 * - **Neither** — rows with no single unit to act on (unresolved, ambiguous)
 *   or physically unreachable (no radio IP).
 *
 * Kept free of imports so the node --test suite can import it directly.
 *
 * Contains:
 * - the status sets and WARNING_STATUSES
 * - initialPreflightSelection / applyRiskyInclusion / allRiskySelected
 * - selectionHasBlockedEntry / countByStatus / preflightHasWarnings
 */

/** Rows the operator may opt into despite the warning. */
export const RISKY_STATUSES = new Set(['mismatch', 'unknown_firmware']);

/** Rows that can be ticked at all. Never includes a blocking status. */
export const SELECTABLE_STATUSES = new Set(['match', 'mismatch', 'unknown_firmware']);

/** Rows no override can ever include — wrong base model. */
export const BLOCKING_STATUSES = new Set(['model_mismatch']);

/** Statuses that make the confirm dialog worth showing. */
export const WARNING_STATUSES = ['model_mismatch', 'mismatch', 'unknown_firmware', 'no_ip', 'unresolved', 'ambiguous'];

const entryList = entries => (Array.isArray(entries) ? entries : []);

/**
 * The rows checked when the confirm dialog opens: clean matches only.
 *
 * @param {Array<{iccid: string, status: string}>} entries
 * @returns {Set<string>} a new set; the caller owns it.
 */
export const initialPreflightSelection = entries =>
  new Set(
    entryList(entries)
      .filter(entry => entry.status === 'match')
      .map(entry => entry.iccid),
  );

/**
 * Tick or untick every risky row, leaving all other rows as they are.
 *
 * Blocking rows are not risky, so the quick action never reaches them.
 *
 * @param {Set<string>} selected - Currently ticked ICCIDs.
 * @param {Array<{iccid: string, status: string}>} entries
 * @param {boolean} include
 * @returns {Set<string>} a new set; `selected` is never mutated.
 */
export const applyRiskyInclusion = (selected, entries, include) => {
  const next = new Set(selected);
  for (const entry of entryList(entries)) {
    if (!RISKY_STATUSES.has(entry.status)) continue;
    if (include) next.add(entry.iccid);
    else next.delete(entry.iccid);
  }
  return next;
};

/**
 * Whether every risky row is currently ticked (false when there are none, so
 * the quick action reads "include" rather than "exclude").
 *
 * @param {Array<{iccid: string, status: string}>} entries
 * @param {Set<string>} selected
 * @returns {boolean}
 */
export const allRiskySelected = (entries, selected) => {
  const risky = entryList(entries).filter(entry => RISKY_STATUSES.has(entry.status));
  return risky.length > 0 && risky.every(entry => selected.has(entry.iccid));
};

/**
 * Whether any ticked row is one the batch must refuse. Selection alone cannot
 * produce this — it is the invariant that keeps a model mismatch off the wire
 * however the selection was built.
 *
 * @param {Array<{iccid: string, status: string}>} entries
 * @param {Set<string>} selected
 * @returns {boolean}
 */
export const selectionHasBlockedEntry = (entries, selected) =>
  entryList(entries).some(entry => BLOCKING_STATUSES.has(entry.status) && selected.has(entry.iccid));

/**
 * How many rows carry a given status.
 *
 * @param {Array<{status: string}>} entries
 * @param {string} status
 * @returns {number}
 */
export const countByStatus = (entries, status) => entryList(entries).filter(entry => entry.status === status).length;

/**
 * Whether the preflight found anything worth confirming before launch.
 *
 * @param {Record<string, number>} summary - Per-status counts.
 * @returns {boolean}
 */
export const preflightHasWarnings = summary => WARNING_STATUSES.some(status => (summary?.[status] ?? 0) > 0);
